import { guid }        from 'rewire-common';
import { observable }  from 'rewire-core';
import { ProductName } from '../DataTypes';
import EditDialogModel from '../schedule/EditDialogModel';

export interface IMasterDetail {
  readonly items:        any[];
  readonly selectedItem: any;

  selectItem(item?: any): void;
  editor?:                EditDialogModel;
}

export interface IMasterDetailState {
  items:        any[];
  selectedItem: any;
}

export default abstract class MasterDetailModel<TState = unknown> implements IMasterDetail {
  private _editor?: EditDialogModel;
  protected state:  IMasterDetailState & TState;
  private _active = false;

  public constructor(editor?: EditDialogModel) {
    this.state = observable({items: [], selectedItem : undefined}) as unknown as IMasterDetailState & TState;

    if (editor) {
      this.editor = editor;
    }
  }

  get editor() {
    return this._editor;
  }

  set editor(value: EditDialogModel | undefined) {
    this._editor = value;
  }

  private handleOnSubmit = () => {
    this._handleOnSubmit();
  };

  protected _handleOnSubmit() {
    if (this._editor && !this._editor.error && this.state.selectedItem) {
      const obj = this._editor.mapToSave();
      Object.assign(this.state.selectedItem, obj);
    }
  }

  protected updateTitle() {
    const newTitle = this.titleText ? `${this.titleText} - ${ProductName}` : `${ProductName}`;
    if (window.document.title !== newTitle) {
      window.document.title = newTitle;
    }
  }

  get titleText() {
    return this.selectedItem?.name ?? '';
  }

  activate() {
    if (!this._active) {
      this._active = true;
      this._editor?.onEvent.addListener('submit', this.handleOnSubmit);
    }
  }

  deactivate() {
    if (this._active) {
      this._active = false;
      this._editor?.onEvent.removeListener('submit', this.handleOnSubmit);
    }
  }

  get items() {
    return this.state.items;
  }

  get selectedItem() {
    return this.state.selectedItem;
  }

  get hasChanges() {
    return this.editor?.form.hasChanges || false;
  }

  get isActive() {
    return this._active;
  }

  protected setItems(items: any[]) {
    if (this.selectedItem) {
      const selectedIdx = items.findIndex(i => i.id === this.selectedItem.id);
      if (selectedIdx >= 0) items.splice(selectedIdx, 1, Object.assign(this.selectedItem, items[selectedIdx]));
    }
    this.state.items.set(items);
  }

  private async loadItem(id?: string) {
    return this.editor?.load(id, false);
  }

  protected async _selectItem(item: any) {
    if (this.state.selectedItem) this.state.selectedItem.selected = false;
    if (item) {
      await this.loadItem(item.id);
    }

    this.state.selectedItem                                       = item;
    if (this.state.selectedItem) this.state.selectedItem.selected = !!item;
    this.updateTitle();
  }

  selectItem(item?: any) {
    this._selectItem(item);
  }

  removeItem = async () => {
    if (!this._editor) return;
    await this._editor.remove();
    if (!this._editor.error) {
      const selectedIdx = this.state.items.findIndex(i => i.id === this.selectedItem.id);
      if (selectedIdx >= 0) this.state.items.splice(selectedIdx, 1);
      this._selectItem(undefined);
    }
  };

  canAddItem(): boolean {
    if (!this._editor || this._editor.form.hasChanges || (this.selectedItem && this._editor.isNew)) {
      return false;
    }

    return true;
  }

  addItem = async (item?: any) => {
    if (!this.canAddItem()) return;
    const newItem = observable({id: guid(), displayName: '<New Item>', ...item});
    this.state.items.push(newItem);
    this.selectItem(newItem);
    if (!item) {
      this._editor!._reset();
    }
  };
}
